import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useT, type MessageKey } from '@/i18n';

import { CloseIcon, ShareIcon } from './Icons';
import tipStyles from './infoTip.module.css';
import styles from './popover.module.css';
import type { SharePopoverProps } from './SharePopover.types';

/** Why Share is off once a secret's views are all used. */
export const NO_VIEWS_LEFT: MessageKey = 'sharePopover.noViewsLeft';
/** Why Share is off once the other device has joined a chat. */
export const CHAT_JOINED: MessageKey = 'sharePopover.chatJoined';

/** On a phone, or in a window too short for a dropdown, the panel is a sheet from the bottom of the screen (see popover.module.css). */
function isSheet(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 560px), (max-height: 520px)').matches;
}

/**
 * Share as a dropdown: the button opens a small panel right under it with
 * Link, QR code, Code and Nearby; on phones the panel is an iOS sheet that
 * rises from the bottom of the screen and can be dragged down by its top to
 * close. Escape, a click outside or the close button shut it, and focus goes
 * back to the button.
 */
export function SharePopover({ children, buttonClassName, title, blocked = null }: SharePopoverProps): React.JSX.Element {
  // a panel open when the last view goes (a device just joined from it) stays until it is closed: it says who joined
  const [panelOpen, setPanelOpen] = useState(false);
  if (blocked !== null && !panelOpen) return <BlockedShare className={buttonClassName} reason={blocked} />;
  return (
    <OpenShare buttonClassName={buttonClassName} title={title} onOpenChange={setPanelOpen}>
      {children}
    </OpenShare>
  );
}

/**
 * Share when the link can let nobody else in: the same button, greyed out. It stays focusable so the reason can be
 * read — a small card above it on hover, on keyboard focus or on a tap (Escape or a tap elsewhere hides it).
 */
function BlockedShare({ className, reason }: { readonly className: string; readonly reason: MessageKey }): React.JSX.Element {
  const t = useT();
  const [tip, setTip] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!tip) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setTip(false);
    };
    const onPointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && wrap.current && !wrap.current.contains(event.target)) setTip(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [tip]);
  return (
    <div
      className={[styles.wrap, styles.blockedWrap].join(' ')}
      ref={wrap}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setTip(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') setTip(false);
      }}
    >
      <button
        type="button"
        className={[className, styles.blocked].join(' ')}
        aria-disabled="true"
        aria-describedby={id}
        onClick={() => {
          // a tap shows the reason; a tap elsewhere or Escape hides it
          setTip(true);
        }}
        onFocus={(event) => {
          if (event.currentTarget.matches(':focus-visible')) setTip(true);
        }}
        onBlur={() => {
          setTip(false);
        }}
        data-testid="share"
      >
        <ShareIcon size={16} />
        <span>{t('common.share')}</span>
      </button>
      {/* always in the document for the screen reader (aria-describedby); shown to the eye on demand */}
      <span id={id} role="tooltip" className={[tipStyles.tip, styles.blockedTip].join(' ')} hidden={!tip} data-testid="share-blocked-tip">
        {t(reason)}
      </span>
    </div>
  );
}

function OpenShare({ children, buttonClassName, title, onOpenChange }: Omit<SharePopoverProps, 'blocked'> & { readonly onOpenChange: (open: boolean) => void }): React.JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();

  // How it was last closed: from inside (focus goes back to the button) or by a click elsewhere (focus stays there).
  const [closedFrom, setClosedFrom] = useState<'inside' | 'outside' | null>(null);
  // Dragging the sheet down by its top on a phone: where the finger started, how far it has gone, when it began.
  const drag = useRef<{ start: number; moved: number; at: number } | null>(null);

  const close = useCallback((returnFocus = true) => {
    setClosedFrom(returnFocus ? 'inside' : 'outside');
    setOpen(false);
  }, []);

  const onDragStart = (event: React.PointerEvent<HTMLDivElement>): void => {
    const sheet = panel.current;
    if (!sheet || !isSheet() || (event.target instanceof Element && event.target.closest('button'))) return;
    drag.current = { start: event.clientY, moved: 0, at: performance.now() };
    event.currentTarget.setPointerCapture(event.pointerId);
    sheet.classList.remove(styles.settling ?? '');
    sheet.classList.add(styles.dragging ?? '');
  };
  const onDragMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const sheet = panel.current;
    if (!drag.current || !sheet) return;
    drag.current.moved = Math.max(0, event.clientY - drag.current.start);
    sheet.style.transform = `translateY(${String(drag.current.moved)}px)`;
  };
  const onDragEnd = (): void => {
    const sheet = panel.current;
    const gesture = drag.current;
    drag.current = null;
    if (!gesture || !sheet) return;
    sheet.classList.remove(styles.dragging ?? '');
    sheet.classList.add(styles.settling ?? '');
    const speed = gesture.moved / Math.max(1, performance.now() - gesture.at);
    // far enough, or flicked: the sheet slides away and closes; otherwise it springs back
    if (gesture.moved > 96 || (gesture.moved > 24 && speed > 0.5)) {
      sheet.style.transform = 'translateY(100%)';
      window.setTimeout(() => {
        close(false);
      }, 260);
    } else {
      sheet.style.transform = '';
    }
  };

  useEffect(() => {
    if (!open && closedFrom === 'inside') button.current?.focus({ preventScroll: true });
  }, [open, closedFrom]);

  useEffect(() => {
    if (!open) return undefined;
    // focus the selected tab, or else the first control of the content
    const body = panel.current?.querySelector<HTMLElement>('[data-popover-body]');
    (body?.querySelector<HTMLElement>('[aria-selected="true"]') ?? body?.querySelector<HTMLElement>('button, a, input'))?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    const onPointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && wrap.current && !wrap.current.contains(event.target)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, close]);

  return (
    <div className={styles.wrap} ref={wrap}>
      <button
        ref={button}
        type="button"
        className={buttonClassName}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? id : undefined}
        onClick={() => {
          if (open) close();
          else {
            setClosedFrom(null);
            setOpen(true);
          }
        }}
        data-testid="share"
      >
        <ShareIcon size={16} />
        <span>{t('common.share')}</span>
      </button>
      {open ? (
        <>
          <div
            className={styles.backdrop}
            aria-hidden="true"
            onClick={() => {
              close(false);
            }}
          />
          <div ref={panel} id={id} className={styles.popover} role="dialog" aria-label={title} data-testid="share-popover">
            <div className={styles.headBox} onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}>
              <span className={styles.grabber} aria-hidden="true" />
              <div className={styles.head}>
                <p className={styles.title}>{title}</p>
                <button
                  type="button"
                  className={styles.close}
                  aria-label={t('common.close')}
                  onClick={() => {
                    close();
                  }}
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            </div>
            <div className={styles.body} tabIndex={0} data-popover-body>
              {children(() => {
                close(false);
              })}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
